// =============================================================================
// Compliance / HACCP READ side — reproduces the kitchen tablet's food-safety
// read screens (cleaning ticks, pest log, probe/fish/emergency/declaration
// forms, and the signed HACCP daily sheet) from Supabase instead of the Google
// Sheet. Sibling of stockReader.js; SAME posture, SAME gate shape, SAME
// anon-session-then-sheet-fallback safety net.
//
// ⚠️ THIS READS THE LEGAL FOOD-SAFETY RECORD. It ships GATED FULLY OFF
// (COMPLIANCE_READ_HOSTS is empty). With the gate off the tablet's behaviour is
// byte-identical to today — every rewired function keeps its existing Sheet
// fetch inside the `else`. A flip is a reviewed one-line host add in daylight,
// after calibration + Adam's dated legal sign-off (COMPLIANCE_READFLIP_PLAN §5).
//
// WHY IT READS THE BASE TABLES, NOT THE CC FEEDS (verified 19/20 Aug 2026).
// The 0180/0181 read feeds (compliance_tick_recent, compliance_cases_feed,
// haccp_day, compliance_periodic_feed) are the COMMAND-CENTRE feeds: each is
// cc_admin-gated (returns null unless the caller's JWT carries
// app_metadata.cc_admin=true) and service_role-grant only. A kitchen tablet's
// anonymous session is NOT a cc_admin session, so those feeds return null to
// it. There is no kiosk-facing (non-cc_admin) compliance feed. So the kiosk
// reader reads the 0009 BASE tables directly — which is possible because their
// RLS is `for select to authenticated using(true)` + a table grant to
// `authenticated`, and the tablet's anonymous sign-in IS the `authenticated`
// role. Names resolve through staff_name(uuid) (execute granted to
// authenticated), exactly as stockReader does. Verified on staging: the four
// 0009 tables carry `ce_read/cc_read/he_read/hd_read` SELECT policies to
// authenticated, and staff_name(uuid) is executable by authenticated.
//
// STATE IS DERIVED, NOT DUMPED. The event logs are append-only; this reader
// projects each event back into the exact row/column shape the tablet parses
// (the Google-Sheet tab schema, `{_ts, ...columns}`), and reconstructs the
// HACCP day object `{sheet:{…}, signOff, status}` by replaying haccp_events in
// order — the same fold handleHaccpEvent does on the old backend.
//
// CONFIG STAYS ON THE SHEET (deliberate — the one reader NOT ported here). The
// admin-authored surface config (getComplianceConfig) has NO Supabase source:
// compliance_config is an inert 0003 stub (0 rows on staging), config authoring
// lives in the Sheet / Command-Centre rebuild, and the plan (§4 scope note, §8.4)
// says config moves WITH the CC config migration, not this legal-record flip.
// So cmpFetchComplianceConfig is left untouched on the Sheet; porting it here
// would mean inventing a data source. Logged as a decision for Adam.
//
// PIN MASKING IS A HARD GATE. The public form readers are the same exposure
// surface as the known pinsForLogin leak. The write path keeps the staff PIN in
// a SEPARATE argument and never spreads it into payload/value (verified in
// complianceSync.js + the boh-kitchen wiring), so Supabase stores no raw PIN —
// only a staff_id uuid we render as a name. Every reconstructed *Pin column is
// therefore emitted as a fully-masked placeholder, and any pin-shaped key that
// ever appears in a passthrough payload is scrubbed on the way out. There is no
// code path in this module that can emit a raw PIN.
// =============================================================================
import { supabase } from "./supabaseClient.js";
import { siteIdForKey, ensureAnonSession } from "./stockSync.js";
import { configAuthoritativeEnabled } from "./configAuthoritative.js";

// --- the compliance read-flip gate (SEPARATE from the write dual-run gate) ----
// Reusing dualRunEnabled would flip the live kitchen the instant this reader is
// wired — NO dark-launch. This gate is EMPTY by default; a flip is a reviewed
// one-line host add in daylight, and (for HACCP) Adam's dated legal sign-off.
// Host-matching hardening (PR #351 review finding 6, 14 Sep 2026). A host
// pasted into one of the *_HOSTS sets below at flip time could carry a
// protocol, a port or a trailing slash by mistake (e.g. someone pastes
// "https://mcr-kitchen.eatrolled.com/" instead of the bare hostname) — a plain
// Set.has() against window.location.hostname would then never match, so the
// flip would silently do nothing and nobody would notice until someone asked
// "why is the tablet still hitting the Sheet?". Normalize BOTH sides (the set
// entry and the live hostname) to the same bare/lowercase/port-free/slash-free
// form before comparing, so a formatting slip can never silently misfire the
// match either way. Pure + exported for the test.
// --- Europe/London day boundaries --------------------------------------------
// The compliance history calendar asks for a range of DAYS ("1–15 July"), but it
// filters rows on `created_at`, which is an instant. Turning a UK day into a pair
// of instants is the whole job, and doing it with a literal "T00:00:00Z" is wrong
// for eight months of the year: during British Summer Time, UK midnight is 23:00
// UTC the day before. That is why this exists — a tick logged at 00:30 on the
// 15th was landing outside a window that asked for the 15th, and a tick logged at
// 00:30 on the 16th was landing inside it. An hour of the day on the wrong sheet,
// in the record an inspector reads.
//
// No timezone library and no hard-coded clock-change dates: the offset is derived
// by asking the platform what the London clock said at that instant. `0009`'s own
// header makes the same point about `entry_date` — never UTC.
// Built on FIRST USE and inside a try/catch, never while the file is being loaded.
// This file is imported at the top of BOTH boh-kitchen.jsx and foh.jsx, so anything
// that throws during module evaluation takes the whole app down — a white screen on
// every till and tablet, caused by a compliance date window that is currently gated
// off entirely. A device without the Europe/London zone data (a cut-down ICU build)
// makes this exact constructor throw, which is why the app's own todayKey() has
// always wrapped it. Falling back to GMT is what the old code effectively did.
let _londonFmt;
function londonPartsFormatter() {
  if (_londonFmt === undefined) {
    try {
      _londonFmt = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/London", hour12: false,
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
      });
    } catch (e) { _londonFmt = null; }
  }
  return _londonFmt;
}

// How far ahead of UTC the London clock is at a given instant, in minutes:
// 0 in winter (GMT), 60 in summer (BST). A device that cannot answer is treated as
// GMT — the same window the old code built, rather than a crash or a nonsense one.
export function londonOffsetMinutes(utcMs) {
  const fmt = londonPartsFormatter();
  if (!fmt) return 0;
  const p = {};
  try {
    for (const part of fmt.formatToParts(new Date(utcMs))) p[part.type] = part.value;
  } catch (e) { return 0; }
  // some engines render midnight as "24" under hour12:false
  const asIfUtc = Date.UTC(+p.year, +p.month - 1, +p.day, (+p.hour) % 24, +p.minute, +p.second);
  return Math.round((asIfUtc - utcMs) / 60000);
}

// The exact UTC instant of a wall-clock moment on a UK calendar day.
// Two passes: guess with the offset at the naive instant, then re-check at the
// candidate, so the two clock-change nights land on the right side. Returns null
// for anything that is not a YYYY-MM-DD date, so the caller can fall back rather
// than build a nonsense window.
export function londonDayBoundaryISO(dateStr, h = 0, min = 0, s = 0, ms = 0) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr == null ? "" : dateStr));
  if (!m) return null;
  const naive = Date.UTC(+m[1], +m[2] - 1, +m[3], h, min, s, ms);
  const firstPass = naive - londonOffsetMinutes(naive) * 60000;
  const settled = naive - londonOffsetMinutes(firstPass) * 60000;
  return new Date(settled).toISOString();
}

export function normalizeHost(h) {
  let s = String(h == null ? "" : h).trim().toLowerCase();
  if (!s) return "";
  s = s.replace(/^[a-z][a-z0-9+.-]*:\/\//, ""); // strip a leading protocol, e.g. "https://"
  s = s.replace(/[/?#].*$/, "");                // strip any path/query/hash (incl. a trailing "/")
  s = s.replace(/:\d+$/, "");                   // strip a trailing ":port"
  return s;
}

function hostGateEnabled(hosts, queryParam) {
  if (typeof window === "undefined") return false;
  try {
    const q = new URLSearchParams(window.location.search).get(queryParam);
    if (q === "on") return true;
    if (q === "off") return false;
  } catch (e) {}
  const current = normalizeHost(window.location.hostname);
  if (!current) return false;
  for (const h of hosts) { if (normalizeHost(h) === current) return true; }
  return false;
}

// Gate for the everyday RECORD readers (cleaning ticks, pest, probe, fish,
// emergency, declaration, corrective) — the Phase-5 "tick" flip.
const COMPLIANCE_READ_HOSTS = new Set([
  // "trafford.eatrolled.com",  // <- (the kitchen is on the Trafford host since 24 Sep 2026 — was mcr-kitchen) added ONLY at the everyday-records flip
]);
export function complianceReadEnabled() { return hostGateEnabled(COMPLIANCE_READ_HOSTS, "compread"); }

// SEPARATE gate for the HACCP DAILY SHEET reader. The chef's day sign-off
// (0009 haccp_days) is the genuine LEGAL signature an inspector opens first, so
// flipping it is higher-stakes than the tick readers and is a distinct,
// deliberate step done AFTER them on Adam's dated legal sign-off (plan Phase 6).
// Its own host set + ?haccpread override means the two flips are INDEPENDENT —
// you can never flip the legal HACCP record just by flipping the everyday ticks.
const HACCP_READ_HOSTS = new Set([
  // "trafford.eatrolled.com",  // <- (the kitchen is on the Trafford host since 24 Sep 2026 — was mcr-kitchen) added ONLY at the HACCP legal-record flip
]);
export function haccpReadEnabled() { return hostGateEnabled(HACCP_READ_HOSTS, "haccpread"); }

// SEPARATE gate for WHICH SUPABASE OBJECT the record/HACCP readers hit — the
// per-site definer feed (migration 1400) vs the 0009 base tables directly. This
// is layered UNDER complianceReadEnabled/haccpReadEnabled above, not instead of
// them: those two decide whether a kiosk's own callers read Supabase AT ALL
// (both empty today, so this module's network functions never run for a real
// kiosk); this one decides HOW they read once they do. It matters on its own
// because the real exposure Lane BW closes lives at the DATABASE layer — a
// plain REST call with the anon key, no app code involved — so the base tables
// get locked to cc_admin-only in a SEPARATE migration (1401) regardless of
// whether any kiosk's JS ever flips compread/haccpread on. This reader must be
// moved onto the feed and PROVEN on every kiosk host BEFORE 1401 can ship, or
// the lock would blind a live kitchen tablet mid-form. EMPTY by default; the OFF
// path (this flag off) is the exact code that reads the base tables today —
// byte-identical, nothing observable changes until a host is added here.
const COMPLIANCE_RECORD_READ_HOSTS = new Set([
  // "trafford.eatrolled.com",  // <- (the kitchen is on the Trafford host since 24 Sep 2026 — was mcr-kitchen) added ONLY once 1400 is on prod and every kiosk's proven
]);
export function complianceRecordFeedEnabled() { return hostGateEnabled(COMPLIANCE_RECORD_READ_HOSTS, "comprecordread"); }

// SEPARATE gate for the admin-authored CONFIG reader (getComplianceConfig — the
// cleaning-task / pest-area / probe / fridge / critical-limit LISTS the forms render).
// This is a THIRD, independent gate: the config is admin-authored and served PUBLICLY
// on mount (before login), whereas the record readers above touch the legal record — so
// the two must never flip together, and this one does NOT need a legal sign-off. EMPTY
// by default; a flip is a reviewed host add once 1200 is on prod, the config is loaded,
// and parity is CLEAN per site. The ON path is guarded at runtime by a drift check
// against the live Sheet (readComplianceConfig below).
const COMPLIANCE_CONFIG_READ_HOSTS = new Set([
  // "trafford.eatrolled.com",  // <- (the kitchen is on the Trafford host since 24 Sep 2026 — was mcr-kitchen) added ONLY at the config read flip (SWITCHON_COMPLIANCE_CONFIG.md)
]);
export function complianceConfigReadEnabled() { return hostGateEnabled(COMPLIANCE_CONFIG_READ_HOSTS, "compconfigread"); }

// FOURTH gate (Lane BV, 13 Sep 2026): the config reader's AUTHORITATIVE mode. A SEPARATE
// host set, EMPTY by default. When a host is listed here (or ?compconfigauth=on on this
// tab) AND it is already on COMPLIANCE_CONFIG_READ_HOSTS, readComplianceConfig serves
// Supabase WITHOUT fetching or comparing the Sheet: no getComplianceConfig call at all.
// This is the step that lets the Command Centre stop writing the Sheet
// (COMPLIANCE_CONFIG_SHEET_WRITE in rolled-admin #219) — with the drift guard still on,
// the first Sheet-less edit would make every flipped tablet fall back to the stale Sheet
// and the edit would never reach a form. If Supabase fails in this mode the reader returns
// null and the screen takes its existing "could not load" path (the same toast a failed
// Sheet fetch shows today) — never a silent fallback to a stale Sheet. A _TEST session is
// the one documented exception: its config lives only on the Sheet, so testMode still
// reads the Sheet in BOTH modes (never the live table).
// Precondition enforced in code (configAuthoritative.js): the host must already read
// Supabase; a listed host that isn't stays guarded and warns once. ?compconfigauth=off
// always wins. Order: read gate → soak → THIS → then the CC Sheet-write flag
// (SWITCHON_COMPLIANCE_CONFIG.md). Config only — the legal-record readers above are untouched.
const COMPLIANCE_CONFIG_AUTHORITATIVE_HOSTS = new Set([
  // "trafford.eatrolled.com",  // <- (the kitchen is on the Trafford host since 24 Sep 2026 — was mcr-kitchen) added ONLY at the config authoritative step, AFTER the host is on COMPLIANCE_CONFIG_READ_HOSTS and has soaked (SWITCHON_COMPLIANCE_CONFIG.md)
]);
export function complianceConfigAuthoritativeEnabled() {
  return configAuthoritativeEnabled({
    hosts: COMPLIANCE_CONFIG_AUTHORITATIVE_HOSTS, param: "compconfigauth",
    readEnabled: complianceConfigReadEnabled(),
    label: "compliance-read", readGate: "COMPLIANCE_CONFIG_READ_HOSTS / ?compconfigread",
  });
}

// --- shared read-source signal (green = Supabase, coral = fell back to Sheet) --
// Compliance spans ~10 forms + the home dashboard, so a single React state var
// won't do; the reader keeps a module-level signal that any screen can read and
// subscribe to, and CmpHome renders a header dot from it.
let _source = null; // null (unknown) | "supabase" | "sheet"
const _sourceSubs = new Set();
export function getComplianceSource() { return _source; }
export function subscribeComplianceSource(fn) {
  if (typeof fn !== "function") return () => {};
  _sourceSubs.add(fn);
  return () => _sourceSubs.delete(fn);
}
function setSource(s) {
  if (s === _source) return;
  _source = s;
  for (const fn of _sourceSubs) { try { fn(s); } catch (e) {} }
}

// Fallback telemetry seam. Every Sheet-fallback is an error path on the legal
// record, so it emits a STRUCTURED event (not just a coral dot a human has to
// notice). Defaults to a machine-parseable console.warn; point _readTelemetry.emit
// at a Worker-forwarding sink at flip time for automated alerting (there is no
// such sink in the app today — this is the seam for one).
export const _readTelemetry = {
  emit(evt) {
    // The tag names what happened: a Sheet FALLBACK (the legal-record readers and the
    // guarded config reader — unchanged), or an AUTHORITATIVE-FAILED config read (Lane
    // BV: nothing fell back; the screen shows its could-not-load error). Kept apart so
    // the runbook's grep for "FALLBACK … reader:\"config\"" stays truthful.
    const tag = (evt && evt.event === "compliance_config_authoritative_failed") ? "AUTHORITATIVE-FAILED" : "FALLBACK";
    try { console.warn("[compliance-read] " + tag + " " + JSON.stringify(evt)); }
    catch (e) { console.warn("[compliance-read] " + tag, evt); }
  },
};
function fellBackToSheet(reader, reason, siteKey, extra) {
  setSource("sheet");
  try { _readTelemetry.emit({ event: "compliance_read_fallback", reader, reason: String(reason), site: canonicalSiteKey(siteKey), ...(extra || {}) }); } catch (e) {}
}

// --- London-time helpers (the sheet backend runs in Europe/London) -----------
function londonParts(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return { date: d.toLocaleDateString("en-CA", { timeZone: "Europe/London" }) }; // YYYY-MM-DD
}
export const londonTodayISO = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });
const toISO = (v) => { if (!v) return ""; try { const d = new Date(v); return isNaN(d.getTime()) ? String(v) : d.toISOString(); } catch (e) { return String(v); } };

// --- site canonicalisation + FAIL CLOSED --------------------------------------
// The kitchen sends 'mcr'; the old backend also answers to 'kitchen' /
// 'manchester kitchen'. Resolve to the stored key in ONE place. An unknown /
// misspelled token must FAIL CLOSED (return null -> the reader falls back to the
// Sheet) — never a global bucket, which the old openSheetFor_ wrongly allowed.
export function canonicalSiteKey(siteKey) {
  const k = String(siteKey || "").trim().toLowerCase();
  if (!k) return "";
  if (k === "kitchen" || k === "manchester kitchen" || k === "mcr") return "mcr";
  return k;
}
async function resolveSiteId(siteKey) {
  const canon = canonicalSiteKey(siteKey);
  if (!canon) return null;                    // fail closed on empty/unknown
  return await siteIdForKey(canon);           // null if the sites table has no such key
}

// --- PIN masking (HARD GATE) --------------------------------------------------
// We never hold a raw PIN (Supabase stores a staff_id uuid, not a PIN), so the
// masked placeholder is fully masked. If a value ever WERE a real PIN, this
// still renders it safe: first-2 + `**`, or `****` if short — matching the old
// ComplianceModule reader exactly. maskPin() is the only thing that ever fills a
// *Pin column.
const MASK = "****";
export function maskPin(v) {
  const s = String(v == null ? "" : v);
  if (!s) return MASK;
  return s.length >= 2 ? s.slice(0, 2) + "**" : MASK;
}
// Defense-in-depth: scrub any pin-shaped key from a passthrough object (used on
// HACCP value/extra, which carry readings/labels — not PINs — but must never be
// trusted to). Matches staffPin, managerPin, adminPin, subjectPin, actorPin,
// raisedByPin, pinPrefix, pin, etc.
const PIN_KEY = /pin\b|pin$|_pin|pin_|staffpin|managerpin|adminpin|subjectpin|actorpin|pinprefix/i;
// A pin-shaped key: the enumerated/suffix forms above, PLUS a camelCase `pin`
// segment (`pinCode`, `staffPinCode`, `pinPrefix`) — `[Pp]in[A-Z]` matches
// only "pin"/"Pin" immediately before an UPPERCASE letter, so ordinary words
// like "spinach"/"topping"/"shipping" are never falsely masked (which would
// corrupt the legal record). No such key exists in the 0009 payloads today;
// this is belt-and-suspenders for any future field.
export function looksLikePinKey(k) {
  const s = String(k);
  return PIN_KEY.test(s) || /[Pp]in[A-Z]/.test(s);
}
export function scrubPins(value) {
  if (Array.isArray(value)) return value.map(scrubPins);
  if (value && typeof value === "object") {
    const out = {};
    for (const k of Object.keys(value)) out[k] = looksLikePinKey(k) ? maskPin(value[k]) : scrubPins(value[k]);
    return out;
  }
  return value;
}

// --- the 7-tab form whitelist + window caps (match the old reader exactly) -----
export const COMPLIANCE_FORM_READER_WHITELIST = new Set([
  "PestLog", "CleaningLog", "CorrectiveActions",
  "ProbeLog", "EmergencyLog", "FishFreezingLog", "DeclarationLog",
]);
// SignOffAudit and HACCP are deliberately excluded (HACCP has its own reader).
export function formInWhitelist(form) { return COMPLIANCE_FORM_READER_WHITELIST.has(String(form || "").trim()); }
export function clampDays(days) { const n = parseInt(days, 10); return (isNaN(n) || n < 1 || n > 90) ? 7 : n; }

// Critical-limit fallbacks (same constants ComplianceModule uses when the admin
// `critical-limits` surface row is missing/empty — Number('') is 0, which would
// silently zero probe tolerance / fish-freeze limit, so an empty reading MUST
// fall back to the constant, never 0). We have no config source here, so we use
// the constants directly; a flip's calibration reconciles against the admin values.
export const CRITICAL_LIMIT_DEFAULTS = {
  "probe-tolerance-c": 1,
  "fish-freezing-critical-c": -20,
  "declaration-cadence-days": 365,
};
function criticalLimit(key) {
  const v = CRITICAL_LIMIT_DEFAULTS[key];
  return (v === "" || v === null || v === undefined) ? 0 : Number(v);
}

// =============================================================================
// PURE PROJECTORS — one compliance_events row (or compliance_cases row) -> the
// tablet's `{_ts, ...sheet-columns}` object. Pure + exported for the unit test.
// nameFor(uuid) -> display name (''), caseFor(row) -> linked case or null.
// Every *Pin column is MASK; role/home aren't stored in Supabase (only a
// staff_id), so they render '' — the screens key on StaffName, and CmpHome uses
// none of them.
// =============================================================================
const num = (v) => (v === "" || v === null || v === undefined ? "" : (isNaN(Number(v)) ? v : Number(v)));

function projectPest(e, ctx) {
  const p = e.payload || {};
  const isDaily = e.event === "pest-daily";
  return {
    _ts: toISO(e.created_at),
    Timestamp: toISO(e.created_at),
    Date: String(e.entry_date || ""),
    Site: ctx.siteKey,
    Event: isDaily ? "daily-check" : "deep-sweep-tick",
    Area: isDaily ? String(p.area || "") : "",
    Evidence: isDaily ? String(p.evidence || "No") : "",
    Details: isDaily ? String(p.details || "") : "",
    CorrectiveAction: isDaily ? String(p.action || "") : "",
    TaskNumber: isDaily ? "" : String(p.taskNumber || ""),
    StaffPin: MASK,
    StaffName: ctx.nameFor(e.staff_id),
    StaffRole: "",
    StaffHome: "",
    // The auto-raised corrective is a SEPARATE compliance_cases row, not
    // back-linked to this event in Supabase (write-path gap — legacy_ref here
    // is null/the batch id, never the corrective id). Emit "" rather than a
    // wrong id; the corrective itself still surfaces in CorrectiveActions.
    // Refute findings F2/F3.
    CorrectiveActionId: "",
  };
}

function projectCleaning(e, ctx) {
  const p = e.payload || {};
  return {
    _ts: toISO(e.created_at),
    Timestamp: toISO(e.created_at),
    WeekStart: String(p.weekStart || ""),
    TaskIndex: p.taskIndex != null ? num(p.taskIndex) : "",
    TaskName: String(p.taskName || ""),
    DayIndex: p.dayIndex != null ? num(p.dayIndex) : "",
    DayDate: String(e.entry_date || ""),   // write path sets entry_date = the cell's day
    Site: ctx.siteKey,
    State: String(p.state || ""),
    StaffPin: MASK,
    StaffName: ctx.nameFor(e.staff_id),
    StaffRole: "",
    StaffHome: "",
    AdminOverride: p.state === "empty" ? "Y" : "",
    TaskId: String(p.taskId || ""),
  };
}

function projectProbe(e, ctx) {
  const p = e.payload || {};
  const target = String(p.method) === "ice" ? 0 : 100;
  const reading = p.reading == null || p.reading === "" ? null : Number(p.reading);
  const errorC = reading == null || isNaN(reading) ? "" : +(reading - target).toFixed(1);
  const tol = criticalLimit("probe-tolerance-c");
  const outOfTol = errorC === "" ? "" : (Math.abs(errorC) > tol ? "Y" : "");
  return {
    _ts: toISO(e.created_at),
    Timestamp: toISO(e.created_at),
    Date: String(e.entry_date || ""),
    Site: ctx.siteKey,
    ProbeId: String(p.probeId || ""),
    ProbeName: String(p.probeName || ""),
    Method: String(p.method || ""),
    Reading: reading == null ? "" : reading,
    Target: target,
    ErrorC: errorC,
    OutOfTolerance: outOfTol,
    Action: String(p.action || ""),
    StaffPin: MASK,
    StaffName: ctx.nameFor(e.staff_id),
    StaffRole: "",
    StaffHome: "",
    // The auto-raised corrective is a SEPARATE compliance_cases row, not
    // back-linked to this event in Supabase (write-path gap — legacy_ref here
    // is null/the batch id, never the corrective id). Emit "" rather than a
    // wrong id; the corrective itself still surfaces in CorrectiveActions.
    // Refute findings F2/F3.
    CorrectiveActionId: "",
  };
}

const EMERGENCY_EVENT = {
  "emergency-fridge-raise": "raise", "emergency-fridge-check": "check",
  "emergency-fridge-disposal": "disposal", "emergency-fridge-close": "close",
};
function projectEmergency(e, ctx) {
  const p = e.payload || {};
  const kind = EMERGENCY_EVENT[e.event] || "";
  const linked = ctx.caseFor(e);
  // Status: mirror the sheet's post-close mutation — the linked case's current
  // status wins; else the event's own snapshot (close = resolved, all else open).
  const status = linked ? linked.status : (kind === "close" ? "resolved" : "open");
  return {
    _ts: toISO(e.created_at),
    Timestamp: toISO(e.created_at),
    IncidentId: String(e.legacy_ref || (linked || {}).legacy_ref || ""),
    Date: String(e.entry_date || ""),
    Site: ctx.siteKey,
    Event: kind,
    Unit: kind === "raise" ? String(p.unit || "") : "",
    FailureTime: kind === "raise" ? String(p.failureTime || "") : "",
    InitialTemp: kind === "raise" ? num(p.initialTemp) : "",
    CheckTemp: kind === "check" ? num(p.checkTemp) : "",
    ActionNotes: String(p.actionNotes || ""),
    Status: status,
    DisposalSigned: kind === "disposal" ? "Y" : "",
    StaffPin: MASK,
    StaffName: ctx.nameFor(e.staff_id),
    StaffRole: "",
    StaffHome: "",
    // The auto-raised corrective is a separate case, not back-linked here
    // (`linked` is the emergency_fridge incident, whose legacy_ref is the
    // INCIDENT id, not the corrective's). Emit "" not a wrong id. Refute F3.
    CorrectiveActionId: "",
  };
}

function projectFish(e, ctx) {
  const p = e.payload || {};
  const isIntake = e.event === "fish-freezing-intake";
  const core = isIntake ? null : (p.coreTemp == null || p.coreTemp === "" ? null : Number(p.coreTemp));
  const crit = criticalLimit("fish-freezing-critical-c");
  const pass = isIntake ? "" : (core == null || isNaN(core) ? "" : (core <= crit ? "Y" : "N"));
  return {
    _ts: toISO(e.created_at),
    Timestamp: toISO(e.created_at),
    BatchId: String(isIntake ? (e.legacy_ref || "") : (p.batchId || "")),
    DateIn: String(e.entry_date || ""),
    Site: ctx.siteKey,
    Event: isIntake ? "intake" : "out",
    Species: isIntake ? String(p.species || "") : "",
    Supplier: isIntake ? String(p.supplier || "") : "",
    BatchRef: isIntake ? String(p.batchRef || "") : "",
    Freezer: isIntake ? String(p.freezer || "") : "",
    TimeIn: isIntake ? toISO(e.created_at) : "",
    TimeOut: isIntake ? "" : toISO(e.created_at),
    CoreTemp: core == null ? "" : core,
    Pass: pass,
    ActionNotes: String(p.actionNotes || ""),
    StaffPin: MASK,
    StaffName: ctx.nameFor(e.staff_id),
    StaffRole: "",
    StaffHome: "",
    // The auto-raised corrective is a SEPARATE compliance_cases row, not
    // back-linked to this event in Supabase (write-path gap — legacy_ref here
    // is null/the batch id, never the corrective id). Emit "" rather than a
    // wrong id; the corrective itself still surfaces in CorrectiveActions.
    // Refute findings F2/F3.
    CorrectiveActionId: "",
  };
}

function projectDeclaration(e, ctx) {
  const p = e.payload || {};
  const cadence = criticalLimit("declaration-cadence-days");
  const acceptedMs = Date.parse(e.created_at || "") || 0;
  const nextDue = acceptedMs ? new Date(acceptedMs + cadence * 86400000).toISOString() : "";
  return {
    _ts: toISO(e.created_at),
    Timestamp: toISO(e.created_at),
    Site: ctx.siteKey,
    Event: "staff-sign",
    DocumentType: String(p.documentType || ""),
    DeclarationVersion: String(p.declarationVersion || ""),
    FsmsVersion: String(p.fsmsVersion || ""),
    AcceptedAt: toISO(e.created_at),
    NextDueAt: nextDue,
    StaffPin: MASK,
    StaffName: ctx.nameFor(e.staff_id),
    StaffRole: "",
    StaffHome: "",
    ManagerPin: "",
    ManagerName: "",
    Notes: String(p.notes || ""),
  };
}

// Map the corrective's stored source token (the triggering event name, or
// 'ad hoc raise') back to the human label the old sheet's Source column carried.
const CORRECTIVE_SOURCE_LABEL = {
  "pest-daily": "Pest (Daily)",
  "pest-deep-sweep-submit": "Pest (Monday Deep Sweep)",
  "probe-calibration": "Probe Calibration",
  "emergency-fridge-raise": "Emergency Fridge",
  "fish-freezing-out": "Fish Freezing (CCP-4)",
  "ad hoc raise": "Ad hoc raise",
};
function correctiveSourceLabel(src) { const s = String(src || ""); return CORRECTIVE_SOURCE_LABEL[s] || s; }

// CorrectiveActions is the compliance_cases (kind='corrective') state, one row
// per case — NOT the event log.
export function projectCorrectiveCase(c, ctx) {
  const d = c.detail || {};
  return {
    _ts: toISO(c.created_at),
    Timestamp: toISO(c.created_at),
    IncidentId: String(c.legacy_ref || c.id || ""),
    Site: ctx.siteKey,
    Source: correctiveSourceLabel(d.source),
    Status: String(c.status || ""),
    Description: String(c.description || ""),
    Action: String(d.action || ""),
    RaisedAt: toISO(c.created_at),   // precise raise instant (opened_date is only a DATE)
    RaisedByPin: MASK,
    RaisedByName: ctx.nameFor(c.raised_by),
    ResolvedAt: c.resolved_at ? toISO(c.resolved_at) : "",
    ResolvedByPin: c.resolved_by ? MASK : "",
    ResolvedByName: ctx.nameFor(c.resolved_by),
    ResolutionAction: String(c.resolution || ""),
    LinkedSheet: "",
    LinkedRow: "",
  };
}

// form -> which events feed it, and its projector. CorrectiveActions is a case
// read (handled separately). Anything not here is not a record form.
const FORM_EVENTS = {
  PestLog: { events: ["pest-daily", "pest-deep-sweep-tick"], project: projectPest },
  CleaningLog: { events: ["cleaning-cell"], project: projectCleaning },
  ProbeLog: { events: ["probe-calibration"], project: projectProbe },
  EmergencyLog: { events: ["emergency-fridge-raise", "emergency-fridge-check", "emergency-fridge-disposal", "emergency-fridge-close"], project: projectEmergency },
  FishFreezingLog: { events: ["fish-freezing-intake", "fish-freezing-out"], project: projectFish },
  DeclarationLog: { events: ["declaration-sign"], project: projectDeclaration },
};

// Pure: project a set of event rows for a form into newest-first tablet rows.
export function projectFormRows(form, events, ctx) {
  const spec = FORM_EVENTS[form];
  if (!spec) return [];
  const rows = (events || []).map((e) => spec.project(e, ctx));
  rows.sort((a, b) => String(b._ts || "").localeCompare(String(a._ts || "")));
  return rows;
}

// =============================================================================
// HACCP DAY reconstruction — replay haccp_events into the same structured object
// the tablet parses. Mirrors handleHaccpEvent's fold + getHaccpToday's shape.
// Pure + exported. events chronological; dayRow = the haccp_days row or null.
// =============================================================================
export function emptyHaccpSheet() {
  return {
    openingChecks: {}, closingChecks: {}, deliveries: [],
    storageTemps: { am: null, pm: null }, storageTempsPerFridge: {},
    cookingEvents: [], ricePh: { am: "", pm: "" }, correctiveActions: "",
  };
}

export function reconstructHaccpDay(siteKey, dateStr, events, dayRow, { nameFor = () => "" } = {}) {
  const hasEvents = (events || []).length > 0;
  if (!hasEvents && !dayRow) {
    return { date: dateStr, site: siteKey, status: "empty", sheet: emptyHaccpSheet(), signOff: null };
  }
  const sheet = emptyHaccpSheet();
  const ordered = (events || []).slice().sort((a, b) =>
    String(a.created_at || "").localeCompare(String(b.created_at || "")) ||
    String(a.id || "").localeCompare(String(b.id || "")));

  for (const e of ordered) {
    const section = String(e.section || "");
    const field = String(e.field || "");
    // value/extra are readings/labels — scrub any pin-shaped key defensively.
    const raw = e.value;
    const value = scrubPins(raw);
    // The old backend stamps {by, pinPrefix, at}. We have no PIN — pinPrefix is
    // the fully-masked placeholder; `by` is the resolved name; `at` = logged.
    const stamp = { by: nameFor(e.staff_id), pinPrefix: MASK, at: toISO(e.created_at) };

    if (section === "opening" || section === "closing") {
      const bucket = section === "opening" ? sheet.openingChecks : sheet.closingChecks;
      if (value) bucket[field] = stamp; else delete bucket[field];
    } else if (section === "delivery") {
      const id = (value && value.id) ? value.id : `sb-${e.id || toISO(e.created_at)}`;
      const delivery = Object.assign({}, value || {}, { id, initials: stamp });
      const at = sheet.deliveries.findIndex((d) => d.id === id);
      if (at >= 0) sheet.deliveries[at] = delivery; else sheet.deliveries.push(delivery);
    } else if (section === "storageTemp") {
      if (field === "am" || field === "pm") {
        sheet.storageTemps[field] = Object.assign({}, value || {}, { initials: stamp });
      } else if (field && field.indexOf("_") !== -1) {
        const parts = field.split("_");
        const fridgeId = parts.slice(0, -1).join("_");
        const slot = parts[parts.length - 1];
        if (fridgeId && (slot === "am" || slot === "pm")) {
          if (!sheet.storageTempsPerFridge[fridgeId]) sheet.storageTempsPerFridge[fridgeId] = {};
          sheet.storageTempsPerFridge[fridgeId][slot] = Object.assign({}, value || {}, { initials: stamp });
        }
      }
    } else if (section === "cooking") {
      const id = (value && value.id) ? value.id : `sb-${e.id || toISO(e.created_at)}`;
      const cook = Object.assign({}, value || {}, { id, initials: stamp });
      const at = sheet.cookingEvents.findIndex((c) => c.id === id);
      if (at >= 0) sheet.cookingEvents[at] = cook; else sheet.cookingEvents.push(cook);
    } else if (section === "ricePh") {
      if (field === "am" || field === "pm") sheet.ricePh[field] = { value, initials: stamp };
    } else if (section === "corrective") {
      sheet.correctiveActions = { text: String(value == null ? "" : value), initials: stamp };
    }
  }

  const signed = dayRow && dayRow.status === "signed";
  const signOff = dayRow
    ? { at: toISO(dayRow.signed_at), name: nameFor(dayRow.signed_by), pinPrefix: MASK }
    : null;
  return {
    date: dateStr, site: siteKey,
    status: signed ? "signed" : "draft",
    sheet, signOff,
  };
}

// =============================================================================
// COMPLIANCE-FEED RPC ERROR REPORTING (PR #351 review finding 5, 14 Sep 2026;
// corrected by Lane CH, 15 Sep 2026 — see CH's PR for the "NEW-1" refute finding
// that caught this). If migration 1400 is ever rolled back AFTER this JS
// deploys — or simply hasn't reached an environment yet — calling one of its
// three RPCs over PostgREST (every call below goes through supabase.rpc(), never
// a raw DB connection) throws PostgREST code PGRST202, "Could not find the
// function public.<name>(...) in the schema cache" — NOT a raw Postgres 42883
// ("function ... does not exist"), which is what this detector originally
// checked for and is not the shape PostgREST puts on the wire. Measured
// directly against staging (mnlcufhljvumllkupizg) 15 Sep 2026 by calling a
// deliberately misspelled RPC name through the anonymous REST path; the exact
// JSON is pasted in Lane CH's PR. The 42883/"does not exist" match is kept
// below (harmless — it just never fires over this transport) in case a future
// caller of this same helper reaches Postgres directly. Every ON-path RPC call
// below already sits inside the existing try/catch in readComplianceFormToday
// / readComplianceFormHistory / readHaccpDay, so the error was ALREADY turned
// into a graceful Sheet fallback (never an unhandled throw, never a silent
// empty — see fellBackToSheet below). What was missing is a LOUD, SPECIFIC
// signal: fellBackToSheet's telemetry is a console.warn shared by every
// fallback reason (bad dates, no site id, a genuine network blip, ...), so a
// rolled-back migration read exactly like every other transient hiccup. This
// reports it as a console.error, naming the RPC and calling out the missing-
// function case explicitly, BEFORE the generic fallback fires. Pure detection/
// message functions + exported for the test; the actual console.error is the
// only side effect.
export function isComplianceFeedRpcMissing(error) {
  if (!error) return false;
  if (error.code === "42883" || error.code === "PGRST202") return true;
  const text = String(error.message || error.details || error.hint || "");
  return /function[\s\S]*does not exist/i.test(text) || /could not find the function/i.test(text);
}
export function describeComplianceFeedRpcError(fnName, error, siteKey) {
  const missing = isComplianceFeedRpcMissing(error);
  const detail = (error && (error.message || error.details)) || String(error == null ? "" : error);
  // Reviewer fix (Lane CH review, 15 Sep 2026): name the error code actually seen
  // instead of a hard-coded "Postgres 42883". Every real caller goes through
  // PostgREST, so the only code that can ever land here in practice is PGRST202
  // (proven on staging above) — hard-coding "42883" made this loud diagnostic
  // say something untrue about the one case it exists to catch. Also names a
  // signature mismatch as a possible cause: PostgREST returns this same
  // PGRST202 shape both when the function is truly absent AND when it exists
  // but this call's argument names no longer match the deployed signature —
  // both are "missing" from the caller's point of view, but only the first is
  // "migration not applied".
  return "[compliance-read] RPC " + fnName + " " +
    (missing
      ? "MISSING (" + (error && error.code ? error.code : "no error code") +
        " -- migration 1400 not applied on this database, rolled back after this build shipped, or this call's arguments no longer match the deployed function)"
      : "ERROR") +
    " site=" + canonicalSiteKey(siteKey) + ": " + detail;
}
function reportComplianceFeedRpcError(fnName, error, siteKey) {
  try { console.error(describeComplianceFeedRpcError(fnName, error, siteKey)); } catch (e) {}
}

// =============================================================================
// NETWORK READERS — Supabase-first with a SHEET FALLBACK on ANY error. The read
// policy is `to authenticated`, so a signed-out session returns ZERO rows — the
// same trap as stock — hence: ensure the anon session first; fall back to the
// Sheet on any throw; fail CLOSED on an unknown site (fall back, never a global
// bucket). Every reader sets the shared _source signal.
// =============================================================================
async function fetchAll(buildQuery) {
  const page = 1000;
  let from = 0, all = [];
  for (;;) {
    const { data, error } = await buildQuery().range(from, from + page - 1);
    if (error) throw error;
    all = all.concat(data || []);
    if (!data || data.length < page) return all;
    from += page;
  }
}

async function staffNamesFor(ids) {
  const map = new Map();
  const unique = [...new Set((ids || []).filter(Boolean))];
  await Promise.all(unique.map(async (id) => {
    try { const { data } = await supabase.rpc("staff_name", { p_id: id }); if (data) map.set(id, data); }
    catch (e) { /* name stays '' */ }
  }));
  return map;
}

// test-mode routing: the old backend reads a separate _TEST tab. Mirror it —
// real mode reads test_mode=false and excludes the qa| idem namespace; test mode
// reads test_mode=true (the qa rows).
function applyTestFilter(q, wantTest) {
  return wantTest ? q.eq("test_mode", true) : q.eq("test_mode", false);
}
// Drop the qa| idem namespace — but ONLY in real mode. In TEST mode every row is
// written test_mode=true AND with a qa| idem key (complianceSync.qaKey), so
// stripping qa| there would empty the result and silently break the test-mode
// calibration the whole flip depends on (refute finding F1).
export const dropQa = (rows, wantTest) =>
  wantTest ? (rows || []) : (rows || []).filter((r) => !(r.idem_key && String(r.idem_key).startsWith("qa|")));

// Fetch the compliance_cases for a site (used by CorrectiveActions and to give
// Emergency rows their derived status). Keyed by legacy_ref and id.
//
// ON path (COMPLIANCE_RECORD_READ_HOSTS): one call to the site-scoped definer
// feed (migration 1400) instead of a direct table select — the feed already
// filters to this ONE site server-side, so a caller can never see another
// site's cases in the same response. OFF path (default): unchanged, byte-
// identical to before this lane.
async function fetchCasesIndex(siteId, wantTest, siteKey) {
  let cases;
  if (complianceRecordFeedEnabled()) {
    const { data, error } = await supabase.rpc("compliance_kiosk_cases_feed", {
      p_site: siteKey, p_test_mode: wantTest, p_gte: null, p_lte: null, p_limit: 5000,
    });
    if (error) { reportComplianceFeedRpcError("compliance_kiosk_cases_feed", error, siteKey); throw error; }
    const raw = (data || []).slice().sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
    cases = dropQa(raw, wantTest);
  } else {
    cases = dropQa(await fetchAll(() => applyTestFilter(
      supabase.from("compliance_cases")
        .select("id, kind, status, opened_date, description, detail, resolution, raised_by, resolved_by, resolved_at, legacy_ref, idem_key, created_at")
        .eq("site_id", siteId), wantTest).order("created_at", { ascending: true })), wantTest);
  }
  const byRef = new Map(), byId = new Map();
  for (const c of cases) { if (c.legacy_ref) byRef.set(String(c.legacy_ref), c); if (c.id) byId.set(String(c.id), c); }
  return { cases, byRef, byId };
}

// Shared body for form-today / form-history over the event forms + the case form.
async function readFormRows(siteKey, form, { wantTest, gte, lte }) {
  const siteId = await resolveSiteId(siteKey);
  if (!siteId) return null;                 // fail closed -> caller falls back to Sheet
  const ctxSiteKey = canonicalSiteKey(siteKey);

  if (form === "CorrectiveActions") {
    const { cases } = await fetchCasesIndex(siteId, wantTest, ctxSiteKey);
    const windowed = cases.filter((c) => {
      const t = c.created_at || "";
      return (!gte || t >= gte) && (!lte || t <= lte);
    });
    const names = await staffNamesFor(windowed.flatMap((c) => [c.raised_by, c.resolved_by]));
    const ctx = { siteKey: ctxSiteKey, nameFor: (id) => names.get(id) || "", caseFor: () => null };
    const rows = windowed
      .filter((c) => c.kind === "corrective")
      .map((c) => projectCorrectiveCase(c, ctx));
    rows.sort((a, b) => String(b._ts || "").localeCompare(String(a._ts || "")));
    return rows;
  }

  const spec = FORM_EVENTS[form];
  if (!spec) return null;
  // ON path: one call to the site-scoped events feed (already newest-first,
  // matching the OFF path's `.order("created_at", {ascending:false})`). OFF
  // path: unchanged — build the query FRESH per page inside the thunk (a
  // PostgREST builder is single-use; reusing one across fetchAll's pages would
  // compound modifiers).
  let events;
  if (complianceRecordFeedEnabled()) {
    const { data, error } = await supabase.rpc("compliance_kiosk_events_feed", {
      p_site: ctxSiteKey, p_events: spec.events, p_test_mode: wantTest,
      p_gte: gte || null, p_lte: lte || null, p_limit: 5000,
    });
    if (error) { reportComplianceFeedRpcError("compliance_kiosk_events_feed", error, ctxSiteKey); throw error; }
    events = dropQa(data || [], wantTest);
  } else {
    const buildEventsQuery = () => {
      let q = supabase.from("compliance_events")
        .select("id, event, family, entry_date, case_id, payload, staff_id, legacy_ref, idem_key, created_at")
        .eq("site_id", siteId).in("event", spec.events);
      if (gte) q = q.gte("created_at", gte);
      if (lte) q = q.lte("created_at", lte);
      return applyTestFilter(q, wantTest).order("created_at", { ascending: false });
    };
    events = dropQa(await fetchAll(buildEventsQuery), wantTest);
  }

  // Emergency rows need the linked case for their derived status.
  let byRef = new Map(), byId = new Map();
  if (form === "EmergencyLog") ({ byRef, byId } = await fetchCasesIndex(siteId, wantTest, ctxSiteKey));
  const names = await staffNamesFor(events.map((e) => e.staff_id));
  const ctx = {
    siteKey: ctxSiteKey,
    nameFor: (id) => names.get(id) || "",
    caseFor: (e) => byRef.get(String(e.legacy_ref)) || byId.get(String(e.case_id)) || null,
  };
  return projectFormRows(form, events, ctx);
}

// getComplianceFormToday, from Supabase. Returns { rows, _source } or null.
export async function readComplianceFormToday(siteKey, form, days, { testMode = false, sheetFetch = null } = {}) {
  const fallback = async (why) => {
    fellBackToSheet("form-today", why, siteKey, { form });
    if (typeof sheetFetch !== "function") return null;
    try { return { rows: await sheetFetch(), _source: "sheet" }; } catch (e) { return null; }
  };
  if (!formInWhitelist(form)) return { rows: [], _source: "supabase" }; // whitelist reject = []
  try {
    await ensureAnonSession();
    const d = clampDays(days);
    const gte = new Date(Date.now() - d * 86400000).toISOString();
    const rows = await readFormRows(siteKey, form, { wantTest: testMode === true, gte, lte: null });
    if (rows == null) return fallback("no site id / unmapped form: " + form);
    setSource("supabase");
    return { rows, _source: "supabase" };
  } catch (e) {
    return fallback("threw: " + (e?.message || e));
  }
}

// getComplianceFormHistory, from Supabase. dateFrom/dateTo inclusive yyyy-MM-dd.
export async function readComplianceFormHistory(siteKey, form, dateFrom, dateTo, { testMode = false, sheetFetch = null } = {}) {
  const fallback = async (why) => {
    fellBackToSheet("form-history", why, siteKey, { form });
    if (typeof sheetFetch !== "function") return null;
    try { return { rows: await sheetFetch(), _source: "sheet" }; } catch (e) { return null; }
  };
  if (!formInWhitelist(form)) return { rows: [], _source: "supabase" };
  const okDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ""));
  if (!okDate(dateFrom) || !okDate(dateTo) || dateFrom > dateTo) return fallback("bad date window");
  // 365-day cap, matching the old reader.
  if (Date.parse(dateTo + "T23:59:59Z") - Date.parse(dateFrom + "T00:00:00Z") > 365 * 86400000) return fallback("window > 365 days");
  try {
    await ensureAnonSession();
    // UK day boundaries, not UTC ones — see londonDayBoundaryISO above. In winter
    // these are byte-for-byte the strings this line used to build; in summer they
    // are an hour earlier, which is the fix.
    const gte = londonDayBoundaryISO(dateFrom, 0, 0, 0, 0);
    const lte = londonDayBoundaryISO(dateTo, 23, 59, 59, 999);
    if (!gte || !lte) return fallback("could not build a London day window");
    const rows = await readFormRows(siteKey, form, { wantTest: testMode === true, gte, lte });
    if (rows == null) return fallback("no site id / unmapped form: " + form);
    setSource("supabase");
    return { rows, _source: "supabase" };
  } catch (e) {
    return fallback("threw: " + (e?.message || e));
  }
}

// haccpToday, from Supabase. Returns the structured { date, site, status, sheet,
// signOff } object (+ _source), or null (caller falls back).
export async function readHaccpDay(siteKey, date, { testMode = false, sheetFetch = null } = {}) {
  const fallback = async (why) => {
    fellBackToSheet("haccp-day", why, siteKey, { date: String(date || "") });
    if (typeof sheetFetch !== "function") return null;
    try { const d = await sheetFetch(); return d ? Object.assign({}, d, { _source: "sheet" }) : null; } catch (e) { return null; }
  };
  try {
    await ensureAnonSession();
    const siteId = await resolveSiteId(siteKey);
    if (!siteId) return fallback("no site id for " + siteKey);
    const dateStr = String(date || londonTodayISO());
    const wantTest = testMode === true;
    // ON path: one call to the site-scoped {events, days} feed (migration 1400) —
    // already ordered exactly like the OFF path (events asc, days desc). OFF
    // path: unchanged, the two direct table selects this reader always used.
    let events, days;
    if (complianceRecordFeedEnabled()) {
      const { data, error } = await supabase.rpc("compliance_kiosk_haccp_feed", {
        p_site: canonicalSiteKey(siteKey), p_date: dateStr, p_test_mode: wantTest,
      });
      if (error) { reportComplianceFeedRpcError("compliance_kiosk_haccp_feed", error, siteKey); throw error; }
      events = (data && data.events) || [];
      days = (data && data.days) || [];
    } else {
      [events, days] = await Promise.all([
        fetchAll(() => applyTestFilter(
          supabase.from("haccp_events")
            .select("id, entry_date, section, field, value, extra, staff_id, idem_key, created_at")
            .eq("site_id", siteId).eq("entry_date", dateStr), wantTest).order("created_at", { ascending: true })),
        fetchAll(() => applyTestFilter(
          supabase.from("haccp_days")
            .select("id, entry_date, status, signed_by, signed_at, idem_key, created_at")
            .eq("site_id", siteId).eq("entry_date", dateStr), wantTest).order("signed_at", { ascending: false })),
      ]);
    }
    const evs = dropQa(events, wantTest);
    const dayRows = dropQa(days, wantTest);
    // prefer a live 'signed' row, else the latest.
    const dayRow = dayRows.find((r) => r.status === "signed") || dayRows[0] || null;
    const names = await staffNamesFor([...evs.map((e) => e.staff_id), dayRow ? dayRow.signed_by : null]);
    const obj = reconstructHaccpDay(canonicalSiteKey(siteKey), dateStr, evs, dayRow, { nameFor: (id) => names.get(id) || "" });
    setSource("supabase");
    return Object.assign({}, obj, { _source: "supabase" });
  } catch (e) {
    return fallback("threw: " + (e?.message || e));
  }
}


// =============================================================================
// COMPLIANCE CONFIG READER (Lane BK) — serve the admin-authored surface config
// (getComplianceConfig) from Supabase (compliance_config_feed, migration 1200)
// INSTEAD of the Google Sheet, behind COMPLIANCE_CONFIG_READ_HOSTS + ?compconfigread.
//
// This is the CONFIG half of the compliance migration — the LISTS the forms render
// (cleaning tasks, pest areas, probes, fridges, critical limits, SOPs, the per-site
// enable matrix), NOT the legal record. It ships gated FULLY OFF.
//
// DRIFT GUARD (mandatory). The forms build from these lists and (for some) write back
// keyed on item ids/labels, so a Supabase config that disagrees with the live Sheet
// would render the wrong lists or file under a stale id. So the reader serves Supabase
// ONLY when it RESOLVES to the same per-site lists as the live Sheet (the exact kiosk
// resolve rule, active-only, IN ORDER). Any drift — or a Sheet we can't reach to verify —
// falls the fetch back to the Sheet. A _TEST session never gets the live config table
// (and vice-versa): testMode refuses the ON path outright.
// =============================================================================

// The kiosk resolve rule, mirrored (cmpResolveSurfaceItems in boh-kitchen.jsx/foh.jsx):
// sites[config.site] || sites[siteKey] || sites['*'], active items only, IN ORDER.
export function resolveConfigItems(config, surfaceKey, siteKey) {
  const surfaces = config && config.surfaces;
  const sf = surfaces && surfaces[surfaceKey];
  if (!sf || !sf.sites) return [];
  const lookup = (config && config.site) || siteKey;
  const bucket = sf.sites[lookup] || sf.sites[siteKey] || sf.sites["*"];
  if (!bucket || !Array.isArray(bucket.items)) return [];
  return bucket.items.filter((i) => i && (i.status || "active") === "active");
}
// Canonical JSON of an item's schema extras (all keys but id/label/status), keys sorted
// so key-order never registers as drift; types preserved so a number that became a
// string IS drift (it would change what the form posts). Mirrors the parity tool.
// Deep, stable canonicalisation: sort object keys at EVERY depth (arrays keep their
// order), values/types preserved. A replacer array in JSON.stringify would instead act
// as a RECURSIVE allow-list and STRIP nested keys — collapsing a {min,max} or a [{...}]
// extra to {} and hiding real drift — so we sort explicitly.
function sortDeep(v) {
  if (Array.isArray(v)) return v.map(sortDeep);
  if (v && typeof v === "object") {
    const o = {};
    for (const k of Object.keys(v).sort()) o[k] = sortDeep(v[k]);
    return o;
  }
  return v;
}
export function canonConfigExtras(item) {
  const out = {};
  for (const k of Object.keys(item || {})) {
    if (k === "id" || k === "label" || k === "status") continue;
    out[k] = item[k];
  }
  return JSON.stringify(sortDeep(out));
}
// A comparison-only signature of a config, for ONE site: per surface, the surface
// label+description + each active item's id+label+extras, IN ORDER. Icons are ignored
// (cosmetic, never written back). Pure + exported for the test.
export function complianceConfigSignature(config, siteKey) {
  const surfaces = (config && config.surfaces) || {};
  const lines = [];
  for (const key of Object.keys(surfaces).sort()) {
    const sf = surfaces[key] || {};
    lines.push("surface " + key + ": label=" + String(sf.label == null ? "" : sf.label).trim() + " | desc=" + String(sf.description == null ? "" : sf.description).trim());
    resolveConfigItems(config, key, siteKey).forEach((it, idx) => {
      lines.push("item " + key + "[" + idx + "]: id=" + String(it.id == null ? "" : it.id).trim() + " label=" + String(it.label == null ? "" : it.label).trim() + " extras=" + canonConfigExtras(it));
    });
  }
  return lines;
}
// Compare the live Sheet config against the Supabase config for one site -> { clean, drift }.
export function complianceConfigParity(sheetCfg, sbCfg, siteKey) {
  const sheet = complianceConfigSignature(sheetCfg, siteKey);
  const supa = complianceConfigSignature(sbCfg, siteKey);
  const sheetSet = new Set(sheet), supaSet = new Set(supa);
  const drift = [];
  for (const l of supa) if (!sheetSet.has(l)) drift.push("supabase has, sheet lacks -> " + l);
  for (const l of sheet) if (!supaSet.has(l)) drift.push("sheet has, supabase lacks -> " + l);
  return { clean: drift.length === 0, drift };
}
// A config is usable only if it carries a surfaces object with at least one surface.
export function configLooksUsable(data) {
  return !!(data && typeof data === "object" && data.surfaces && typeof data.surfaces === "object" && Object.keys(data.surfaces).length > 0);
}

// The reader. Returns a config object to serve (Supabase when CLEAN, else the Sheet), or
// null only when neither can be produced (Sheet unreachable for the parity check) — the
// caller then keeps its last-known cache / falls back. NEVER serves a config that
// disagrees with the live Sheet. testMode refuses the live table outright.
// AUTHORITATIVE mode (Lane BV; `authoritative` defaults to the runtime gate, false
// everywhere today): Supabase is served WITHOUT the Sheet fetch/compare, and null on any
// failure (the caller's "could not load" path — never the Sheet). `rpc` / `ensureSession`
// are injectable for the network-free unit test; their defaults are exactly today's calls.
export async function readComplianceConfig({
  site, testMode = false, sheetFetch = null,
  authoritative = complianceConfigAuthoritativeEnabled(),
  rpc = (n, a) => supabase.rpc(n, a),
  ensureSession = ensureAnonSession,
} = {}) {
  const sheetOnce = async () => {
    if (typeof sheetFetch !== "function") return null;
    try { return await sheetFetch(); } catch (e) { return null; }
  };
  // A _TEST config must never be served from the live table (and a live config must never
  // reach a test session) — refuse the ON path when test mode is on. This holds in
  // authoritative mode too: the test config has no Supabase home, so a test session is
  // the one documented case that still reads the Sheet (BV-1 in DECISIONS_NEEDED.md).
  if (testMode === true) { fellBackToSheet("config", "testMode on — refusing the live config table", site); return sheetOnce(); }

  if (authoritative === true) {
    // AUTHORITATIVE: Supabase IS the config. No Sheet fetch, no parity compare, and on
    // failure NO fallback (the Sheet may be stale once the CC stops writing it) — return
    // null so the screen takes its existing "could not load" path. The source signal is
    // left as it was (nothing fell back to the Sheet, so no coral dot); the failure goes
    // out as a structured telemetry event + a plain console line.
    const failed = (reason) => {
      try { console.warn("[compliance-read] AUTHORITATIVE config read failed — no Sheet fallback in this mode; the screen shows its usual could-not-load error: " + reason); } catch (e) {}
      try { _readTelemetry.emit({ event: "compliance_config_authoritative_failed", reader: "config", reason: String(reason), site: canonicalSiteKey(site) }); } catch (e) {}
      return null;
    };
    try {
      await ensureSession();
      const { data, error } = await rpc("compliance_config_feed", { p_site: site });
      if (error) return failed("supabase read failed: " + (error.message || error));
      if (!configLooksUsable(data)) return failed("supabase returned an empty/invalid config");
      setSource("supabase");
      return Object.assign({}, data, { _source: "supabase", _parity: "authoritative" });
    } catch (e) { return failed("threw: " + (e && e.message ? e.message : e)); }
  }

  let sbCfg = null;
  try {
    await ensureSession();
    const { data, error } = await rpc("compliance_config_feed", { p_site: site });
    if (error) { fellBackToSheet("config", "supabase read failed: " + (error.message || error), site); return sheetOnce(); }
    if (!configLooksUsable(data)) { fellBackToSheet("config", "supabase returned an empty/invalid config", site); return sheetOnce(); }
    sbCfg = data;
  } catch (e) { fellBackToSheet("config", "threw: " + (e && e.message ? e.message : e), site); return sheetOnce(); }

  // Supabase is usable — but only serve it if it RESOLVES to the same lists as the live
  // Sheet (the drift guard). Fetch the Sheet ONCE, use it for both parity + fallback.
  const sheetCfg = await sheetOnce();
  if (!configLooksUsable(sheetCfg)) {
    // can't reach the Sheet to verify -> do NOT trust a possibly-stale Supabase config.
    fellBackToSheet("config", "sheet unreachable/empty for the parity check", site);
    return null;
  }
  const p = complianceConfigParity(sheetCfg, sbCfg, site);
  if (!p.clean) {
    fellBackToSheet("config", "supabase/sheet drift", site, { drift: p.drift.slice(0, 8) });
    return Object.assign({}, sheetCfg, { _source: "sheet", _parity: "drift" });
  }
  setSource("supabase");
  return Object.assign({}, sbCfg, { _source: "supabase", _parity: "clean" });
}
